import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ArrowRight, Sun, Moon, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HRLogo from './HRLogo';
import { useTheme } from '../contexts/ThemeContext';

const WaitlistHeader: React.FC = () => {
    const { t } = useTranslation('marketing');
    const { resolvedTheme, setTheme } = useTheme();
    const isDark = resolvedTheme === 'dark';
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const navLinks = [
        { href: '/manifesto', label: t('manifesto.header_link', 'Manifesto') },
        { href: '/whitepaper', label: t('whitepaper.header_link', 'Whitepaper') },
        { href: '/privacy',   label: t('waitlist.nav_trust', 'Privacy'), icon: true },
    ];

    return (
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-hig-border bg-hig-bg-secondary/80 backdrop-blur-xl backdrop-saturate-150">
            <div dir="ltr" className="container-hig flex items-center justify-between h-16">

                {/* Logo */}
                <a href="/" className="flex items-center gap-2.5 group flex-shrink-0">
                    <HRLogo size={26} state="default" showGlow={false} />
                    <span className="text-[15px] font-medium tracking-tight">
                        <span className="text-hig-text-primary group-hover:opacity-80 transition-opacity">HumanReplica</span>
                        <span className="text-hig-accent">.ai</span><span className="text-[15px] text-hig-text-secondary -translate-y-[3px] inline-block ml-[1px] font-normal">®</span>
                    </span>
                </a>

                {/* Desktop: Nav links + Controls */}
                <div className="hidden md:flex items-center gap-5">
                    {navLinks.map(link => (
                        <a
                            key={link.href}
                            href={link.href}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-hig-text-secondary hover:text-hig-accent transition-colors"
                        >
                            {link.label}
                            {link.icon && <ArrowRight className="w-3 h-3" />}
                        </a>
                    ))}

                    <div className="w-px h-5 bg-hig-border" />

                    <button
                        onClick={() => setTheme(isDark ? 'light' : 'dark')}
                        title={isDark ? 'Switch to light' : 'Switch to dark'}
                        className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary"
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>

                    <LanguageSwitcher />
                </div>

                {/* Mobile: Theme + Burger */}
                <div className="flex md:hidden items-center gap-2">
                    <button
                        onClick={() => setTheme(isDark ? 'light' : 'dark')}
                        className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors text-hig-text-secondary hover:text-hig-text-primary"
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="flex items-center justify-center w-9 h-9 rounded-lg text-hig-text-primary"
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile dropdown menu */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                        className="md:hidden overflow-hidden border-t border-hig-border bg-hig-bg-secondary/95 backdrop-blur-xl"
                    >
                        <div className="container-hig py-4 flex flex-col gap-1">
                            {navLinks.map((link, i) => (
                                <motion.a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setIsMenuOpen(false)}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="flex items-center gap-2 py-3 text-sm font-medium text-hig-text-primary hover:text-hig-accent transition-colors border-b border-hig-border/50 last:border-0"
                                >
                                    {link.label}
                                    {link.icon && <ArrowRight className="w-3.5 h-3.5" />}
                                </motion.a>
                            ))}
                            <div className="pt-3">
                                <LanguageSwitcher />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default WaitlistHeader;
